import { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router';
import Icon from '../components/Icon';
import { grams, kcal } from '../lib/format';
import { parseISODate, shortDate } from '../lib/dates';
import { useApp, useAppPath } from '../lib/AppContext';

// "24/10/2026"
const fullDate = (iso) => `${shortDate(iso)}/${parseISODate(iso).getFullYear()}`;

const range = (p) => {
  if (!p.effective_to) return `Từ ${fullDate(p.effective_from)}`;
  if (p.effective_to === p.effective_from) return `${fullDate(p.effective_from)} · 1 ngày`;
  const sameYear = p.effective_from.slice(0, 4) === p.effective_to.slice(0, 4);
  return `${sameYear ? shortDate(p.effective_from) : fullDate(p.effective_from)} – ${fullDate(p.effective_to)} · ${p.days} ngày`;
};

const sourceText = (p) => {
  if (p.source === 'ai_suggestion') return p.label ? `AI gợi ý · mục tiêu ${p.label}` : 'AI gợi ý';
  if (p.source === 'initial') return 'Mục tiêu ban đầu';
  if (p.source === 'coach') return p.label || 'Coach đặt';
  return 'Tự nhập';
};

function Period({ period: p }) {
  const highlighted = p.current || p.upcoming;
  const protein = [
    p.protein_goal ? `Đạm ${grams(p.protein_goal)}` : null,
    p.workout_protein_goal && p.workout_protein_goal !== p.protein_goal ? `ngày tập ${grams(p.workout_protein_goal)}` : null,
  ].filter(Boolean).join(' · ');
  // Out of the period's days so far; a period with nothing logged has nothing to report.
  const met = p.days_logged > 0 && p.days_met != null ? `đạt mục tiêu ${p.days_met}/${p.days} ngày` : null;
  return (
    <li className="relative pb-3 pl-7">
      <span aria-hidden="true" className={`absolute left-[5px] top-6 size-3 rounded-full border-2 ${highlighted ? 'border-primary bg-primary' : 'border-base-300 bg-base-100'}`} />
      <div className={`rounded-[18px] bg-base-100 p-4 ${highlighted ? 'border-2 border-primary' : ''}`}>
        <div className="flex items-start justify-between gap-2">
          <span className={`text-xs font-semibold ${highlighted ? 'text-brand-text' : 'text-muted'}`}>{range(p)}</span>
          {p.current && <span className="shrink-0 rounded-full bg-primary px-2 py-0.5 text-[11px] font-bold text-primary-content">Đang áp dụng</span>}
          {p.upcoming && <span className="shrink-0 rounded-full bg-[#fdf3d4] px-2 py-0.5 text-[11px] font-bold text-[#8a6100]">Sắp áp dụng</span>}
        </div>
        <div className="tabular mt-1 text-xl font-extrabold">{p.calorie_goal ? `${kcal(p.calorie_goal)} kcal` : 'Chưa đặt calo'}</div>
        {protein && <div className="tabular text-sm">{protein}</div>}
        <div className="mt-1 flex items-center gap-1 text-xs text-muted">
          {p.source === 'ai_suggestion' && <Icon name="sparkles" className="size-3.5 text-brand-text" />}
          <span>{[sourceText(p), met].filter(Boolean).join(' · ')}</span>
        </div>
      </div>
    </li>
  );
}

// "Lịch sử mục tiêu": a timeline of the daily targets, newest first.
export default function TargetsPage() {
  const { api } = useApp();
  const path = useAppPath();
  const navigate = useNavigate();
  const location = useLocation();
  const [periods, setPeriods] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    api.get('/targets').then((d) => setPeriods(d.periods)).catch((e) => setError(e.message));
  }, [api]);

  const back = () => (location.key !== 'default' ? navigate(-1) : navigate(path('/settings')));

  return (
    <div className="flex flex-col gap-4">
      <div className="pt-safe flex items-center gap-1 pt-4">
        <button type="button" className="btn btn-circle btn-ghost -ml-2 size-11" aria-label="Quay lại" onClick={back}>
          <Icon name="chevronLeft" className="size-6" />
        </button>
        <h1 className="text-[22px] font-bold">Lịch sử mục tiêu</h1>
      </div>

      {error && <div role="alert" className="rounded-[20px] bg-[#fdebe4] p-4 text-sm text-[#8e3414]">{error}</div>}
      {!periods && !error && (
        <div className="flex flex-col gap-3" aria-busy="true">
          {[0, 1, 2].map((i) => <div key={i} className="skeleton h-28 rounded-[18px]" />)}
        </div>
      )}
      {periods && periods.length === 0 && (
        <p className="rounded-[20px] bg-base-100 p-5 text-sm text-muted">Bạn chưa đặt mục tiêu nào.</p>
      )}
      {periods && periods.length > 0 && (
        <ol className="relative before:absolute before:bottom-6 before:left-[10px] before:top-6 before:w-px before:bg-base-300">
          {periods.map((p) => <Period key={p.effective_from} period={p} />)}
        </ol>
      )}

      <p className="flex items-start gap-2 px-1 text-xs text-muted">
        <Icon name="info" className="mt-0.5 size-4 shrink-0" />
        Đổi mục tiêu không làm thay đổi kết quả các ngày trước. Mỗi ngày luôn được so với mục tiêu đang áp dụng vào ngày đó.
      </p>
    </div>
  );
}
